import Head from 'next/head';
import { organization } from 'lib/seo/constants';
import useDictionary from 'lib/hooks/useDictionary';
import { IRecipeSchema } from 'lib/seo/interfaces';
import { formatDateYMD, sitecoreDateToIsoWithOffset } from 'lib/utils/date-formatter';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { RecipeResponse } from 'lib/services/models/content-response';
import { toAbsoluteUrl } from 'lib/seo/seoComponentsSchema';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import React, { useEffect, useState } from 'react';
import {
  buildSitecoreMediaUrl,
  getExternalLinkUrl,
  parseImageSize,
} from 'components/helpers/Global/SitecoreImage/sitecoreMedia';

function stripHtml(value?: string | null): string {
  return value ? value.replace(/<[^>]+>/g, '').trim() : '';
}

export default function RecipeSchema({
  time,
  ingredients,
  directions,
  title,
  imageUrl,
  publishedDate,
  servings,
  recipeVideo,
  recipeVideoTitle,
  recipeVideoDescription,
  recipeVideoPublishedDate,
  recipeCategory,
  recipeCuisine,
  keywords,
  nutrition,
  canonical,
}: IRecipeSchema) {
  const { getDictionaryValue } = useDictionary();

  const { locale } = useGlobalData();
  const { contentItem, contributor } = useSitecoreContext().sitecoreContext;
  const recipe =
    contentItem && (contentItem as RecipeResponse).ContentType === 'Recipe'
      ? (contentItem as RecipeResponse)
      : null;

  //fetch the video data
  const [videoData, setVideoData] = useState<{
    videoId?: string;
    videoThumbnailUrl?: string;
    videoTitle?: string;
    videoThumbnail?: string;
    videoURL?: string;
    videoDescription?: string;
    videoUploadDate?: string;
    videoHeadline?: string;
  }>({});

  const videoId = videoData.videoId;
  const videoThumbnail = videoData.videoThumbnailUrl;
  const videoDescription = videoData.videoDescription;
  const videoUplaodDate = videoData.videoUploadDate;
  const videoHeadline = videoData.videoHeadline;
  useEffect(() => {
    if (!recipe?.ContentId) return;
    const fetchVideoData = async () => {
      try {
        const response = await fetch(
          `/api/video/video-data?recipeContentId=${recipe.ContentId}&language=${locale}`
        );
        if (!response.ok) return;
        const data = await response.json();
        const video = data?.videos?.[0];
        if (!video) return;
        const { width, height } = parseImageSize(video.videoThumbnail?.value);
        setVideoData({
          videoId: video.videoId?.value || '',
          videoTitle: video.name?.value || '',
          videoDescription: video.videoDescription?.value || '',
          videoThumbnailUrl:
            getExternalLinkUrl(video.videoThumbnailExternal?.value) ||
            buildSitecoreMediaUrl(video.videoThumbnail?.value, {
              host: process.env.NEXT_PUBLIC_SITECORE_HOST,
              width: width ? Number(width) : 560,
              height: height ? Number(height) : 315,
            }),
          videoURL: video.videoURL?.jsonValue?.value,
          videoUploadDate: sitecoreDateToIsoWithOffset(video.uploadDate?.value),
          videoHeadline: video.videoHeadline?.value,
        });
      } catch (e) {
        console.error('Video fetch failed', e);
      }
    };

    fetchVideoData();
  }, [recipe?.ContentId, locale]);

  //  Extract Rich Snippet Kitchen Tip
  const kitchenSnippet =
    recipe?.KitchenTips?.find((tip) => tip.Category === 'Rich Snippet')?.Description ||
    recipe?.Description ||
    '';
  const contributorData = contributor as {
    authorName?: string;
    authorProfileURL?: string;
    isOrganization?: boolean;
  };
  const prepTimeH = time?.prepTimeHours,
    prepTimeM = time?.prepTimeMinutes,
    totalTimeH = time?.totalTimeHours,
    totalTimeM = time?.totalTimeMinutes;

  const ingredientList = ([] as string[]).concat(
    ...ingredients.map((group) => {
      return group.ingredientList.map((ingredient) => {
        const quantity = ingredient.ingredientQuantity ?? '';
        return `${quantity} ${ingredient.ingredientName}`;
      });
    })
  );

  const stepNameTemplate =
    getDictionaryValue('Components.Recipe.RecipeContentInstructions.StepNumber') || 'Step {number}';

  const baseUrl = toAbsoluteUrl(recipe?.PageUrl ?? '', process.env.PUBLIC_URL);
  const authorName =
    contributorData?.authorName?.trim() || recipe?.AuthorAttribution?.trim() || organization;
  const authorUrl = contributorData?.authorProfileURL
    ? toAbsoluteUrl(contributorData.authorProfileURL, process.env.PUBLIC_URL)
    : undefined;

  const prepMethods = recipe?.PrepMethods?.length
    ? [...recipe.PrepMethods].sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0))
    : null;

  const stepsFromPrepMethods = prepMethods
    ? prepMethods.map((method, index) => {
        const stepNumber = index + 1;
        const stepId = `step${stepNumber}`;
        const stepName = stepNameTemplate.replace('{number}', String(stepNumber));
        const step: Record<string, unknown> = {
          '@type': 'HowToStep',
          name: stepName,
          text: stripHtml(method.Description),
        };
        const imgUrl = method.StepShots?.[0]?.Url;
        if (imgUrl) {
          const absoluteImgUrl = toAbsoluteUrl(imgUrl, baseUrl);
          if (absoluteImgUrl) step.image = absoluteImgUrl;
        }

        if (baseUrl) {
          step.url = `${baseUrl}#${stepId}`;
        }

        return step;
      })
    : [];

  let directionStepIndex = 0;
  const stepsFromDirections = !stepsFromPrepMethods.length
    ? directions.flatMap((section) =>
        section.directionList.map((step) => {
          directionStepIndex += 1;
          return {
            '@type': 'HowToStep',
            name: stepNameTemplate.replace('{number}', String(directionStepIndex)),
            text: stripHtml(step.stepDescription),
          };
        })
      )
    : [];

  const nutritionValue = nutrition
    ? {
        '@type': 'NutritionInformation',
        ...nutrition,
      }
    : '';
  let prepTimeValue = null;

  if (prepTimeH || prepTimeM) {
    prepTimeValue = 'PT';
    prepTimeValue += prepTimeH ? prepTimeH + 'H' : '0H';
    prepTimeValue += prepTimeM ? prepTimeM + 'M' : '0M';
  }

  let totalTimeValue = null;

  if (totalTimeH || totalTimeM) {
    totalTimeValue = 'PT';
    totalTimeValue += totalTimeH ? totalTimeH + 'H' : '0H';
    totalTimeValue += totalTimeM ? totalTimeM + 'M' : '0M';
  }

  const recipeInstructionsValues = [
    ...(stepsFromPrepMethods.length ? stepsFromPrepMethods : stepsFromDirections),
  ];

  const hasVideo = recipeVideo || !!videoId;
  const recipeVideoValue = hasVideo
    ? {
        '@type': 'VideoObject',
        name: videoHeadline || recipeVideoTitle,
        description: videoDescription || recipeVideoDescription,
        thumbnailUrl: videoThumbnail || '',
        embedUrl: `https://www.youtube.com/embed/${videoId}` || '',
        uploadDate: videoUplaodDate || recipeVideoPublishedDate,
      }
    : null;

  const schemaRecipe = {
    '@context': 'http://schema.org',
    '@type': 'Recipe',
    '@id': canonical || '',
    name: title,
    image: [imageUrl],
    description: kitchenSnippet,
    author: {
      '@type': contributorData?.isOrganization ? 'Organization' : 'Person',
      name: authorName,
      ...(authorUrl ? { url: authorUrl } : {}),
    },
    datePublished: formatDateYMD(publishedDate),
    dateModified: formatDateYMD(recipe?.SEOModifiedDate || ''),
    ...(prepTimeValue && { prepTime: prepTimeValue }),
    ...(totalTimeValue && { totalTime: totalTimeValue }),
    ...(servings && { recipeYield: servings }),
    nutrition: nutritionValue,
    recipeIngredient: ingredientList,
    recipeInstructions: recipeInstructionsValues,
    ...(hasVideo && { video: recipeVideoValue }),
    ...(recipeCategory && { recipeCategory }),
    ...(recipeCuisine && { recipeCuisine }),
    ...(keywords && { keywords }),
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaRecipe) }}
      />
    </Head>
  );
}
